import { Dispatch, FC, SetStateAction, useEffect, useLayoutEffect, useRef, useState } from 'react';
import * as Sentry from '@sentry/react';
import { LearnosityRequest } from '../../models/LearnosityRequest';
import { useNavigate, useParams } from 'react-router-dom';
import { assessmentSubmitted } from '../../bffApiCalls/assessmentSubmitted';
import { AttemptInformation, attemptInfoFromScores } from '../../models/AttemptInformation';
import { PreviousAttempts } from '../../models/PreviousAttempts';
import { mapSkillStatus } from '../../businessLogicService/mapSkillStatus';
import { useAuth0 } from '@auth0/auth0-react';
import { isTokenExpired } from '../../utils/isTokenExpired';
import refreshTokenOrLogout from '../../utils/refreshTokenOrLogout';
import { AssessmentState } from '../../custom-hooks/useLrnSession';
import { getAssessmentStatus } from '../../bffApiCalls/getAssessmentStatus';
import { PreviousStatus } from '../StudentSingleSkill/StudentSingleSkill';
import { useFlag } from '@unleash/proxy-client-react';
import { LTS_SHUTOFF_PP_435_WEEKLY_GOAL_FLAG, useWeeklyGoal } from '../../contexts/WeeklyGoalContext';
import { GoalValue } from '../WeeklyGoal/WeeklyGoalWrapper';
import { PencilRuler } from 'lucide-react';
import { Button } from 'primereact/button';
import { SkillResourceSteps } from '../../models/Timeline';
import EphemeralMessage from '../StudentSingleSkill/EphemeralMessage';
import { Dialog } from 'primereact/dialog';
import { SubmitConfirmationModalContent } from './SubmitConfirmationModalContent';
import { ThreeDotLoader } from '../Fallback/ThreeDotsLoader';
import { useIsCountdownFlagActive } from '../../businessLogicService/retakeCountdownHelper';
import { useCooldown } from '../../contexts/RetakeCountdownContext';
import { usePostHog } from '@posthog/react';
import { usePreviewMode } from '../../contexts/PreviewModeContext';
import { LearnosityScores } from '../../models/LearnosityScores';
import { useObservationLogger } from '../../experiments/useObservationLogger';
import { useSuspiciousActivityTracking } from '../../custom-hooks/useSuspiciousActivityTracking';
import { useExitFullscreenOnUnmount } from '../../custom-hooks/useExitFullscreenOnUnmount';
import { useCopyProtection } from '../../custom-hooks/useCopyProtection';
import { captureLearnosityException } from '../../utils/learnositySentry';
import { PASSING_SCORE } from '../../businessLogicService/weeklyGoalEligibility';
import {
  useLearnosityItemsInitialization,
  type LearnosityInitializationRefetch,
} from '../../custom-hooks/useLearnosityItemsInitialization';
import { saveLearnosityResponses } from '../../utils/learnositySave';
import { LearnosityMathRecoveryNotice } from '../LearnosityMathRecoveryNotice/LearnosityMathRecoveryNotice';

interface AssessmentWrapperProps {
  request: LearnosityRequest;
  refetchInitialization: LearnosityInitializationRefetch;
  onFailure?: (error: unknown) => void;
  changeState: (state: AssessmentState) => void;
  sessionId: string;
  setAttemptInfo: (attempt: AttemptInformation) => void;
  setLoading: (s: boolean) => void;
  setCurrentStatus: (value: { classname: string; status: string | undefined } | null) => void;
  previousAttempts: PreviousAttempts[];
  setPreviousAttempts: Dispatch<SetStateAction<PreviousAttempts[]>>;
  setAlreadySubmitted: Dispatch<SetStateAction<boolean>>;
  setPreviousStatus: Dispatch<SetStateAction<PreviousStatus[]>>;
  setDisabledReviewModal: Dispatch<SetStateAction<boolean>>;
  isWeeklyGoalElegible?: boolean;
  weeklyGoalOverrideData?: GoalValue | null;
  setAssessmentInProgress: Dispatch<SetStateAction<boolean>>;
  setCurrentStep: Dispatch<SetStateAction<SkillResourceSteps | null>>;
  skillId?: string;
}

const AssessmentWrapper: FC<AssessmentWrapperProps> = ({
  request,
  refetchInitialization,
  onFailure,
  changeState,
  sessionId,
  setAttemptInfo,
  setLoading,
  setCurrentStatus,
  previousAttempts,
  setPreviousAttempts,
  setAlreadySubmitted,
  setPreviousStatus,
  setDisabledReviewModal,
  isWeeklyGoalElegible,
  weeklyGoalOverrideData,
  setAssessmentInProgress,
  setCurrentStep,
  skillId,
}) => {
  const [loadingExit, setLoadingExit] = useState(false);
  const [confirmationModalState, setConfirmationModalState] = useState(false);
  const [incompleteQuestions, setIncompleteQuestions] = useState(0);
  const [disableSubmition, setDisableSubmition] = useState(true);
  const playerHostRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { skillCode } = useParams();
  const { getAccessTokenSilently, logout } = useAuth0();
  const isWeeklyGoalEnabled = !useFlag(LTS_SHUTOFF_PP_435_WEEKLY_GOAL_FLAG);
  const ctx = useWeeklyGoal();
  const cooldownCtx = useCooldown();
  const isRetakeCountdownFlagActive = useIsCountdownFlagActive();
  const posthog = usePostHog();
  const previewMode = usePreviewMode();
  const [scores, setScores] = useState<LearnosityScores | null>(null);
  const [submitComplete, setSubmitComplete] = useState<boolean>(false);
  const { logObservations } = useObservationLogger();
  useSuspiciousActivityTracking({ assessmentSessionId: sessionId, skillId });
  useExitFullscreenOnUnmount();
  useCopyProtection();

  const checkTokenRefresh = async () => {
    if (isTokenExpired()) {
      await refreshTokenOrLogout(getAccessTokenSilently, logout, posthog);
    }
  };

  const saveAssessmentProgress = async () => {
    try {
      const response = await getAssessmentStatus(
        request.request.user_id,
        request.request.session_id,
        getAccessTokenSilently,
        logout,
        posthog,
      );
      if (response?.status === 'Completed') {
        return setAlreadySubmitted(true);
      }
      itemsAppRef.current?.save();
    } catch (e) {
      captureLearnosityException(e, {
        api: 'items',
        operation: 'save_progress',
        assessmentType: 'skill',
        activityReference: request.request.activity_id,
        renderingType: request.request.rendering_type,
        assessmentState: request.request.state,
        learnerId: request.request.user_id,
        sessionId: request.request.session_id,
      });
    }
  };

  const verifyResponsesAndOpenConfirmation = () => {
    const responses = itemsAppRef.current?.getResponses();
    if (responses) {
      const unanswered = 5 - Object.keys(responses).length;
      setIncompleteQuestions(unanswered);
    }
    setConfirmationModalState(true);
  };

  const backToPracticeHandler = async () => {
    if (!previewMode && itemsAppRef.current) await saveAssessmentProgress();
    setCurrentStep(SkillResourceSteps.PRACTICE_PROBLEMS);
    setAssessmentInProgress(false);
  };

  const saveAndExitHandler = async () => {
    setLoadingExit(true);
    await saveAssessmentProgress();
    setTimeout(() => {
      setLoadingExit(false);
      navigate('/');
    }, 1500);
  };

  const { status, itemsAppRef, mathRecovery, retryMathRecovery } = useLearnosityItemsInitialization({
    reloadOn10001AfterReady: true,
    initObj: request,
    restartKey: previewMode ? 'preview' : 'assessment',
    assessmentType: 'skill',
    refetchInitialization,
    onFailure,
    // Preview never writes back, and a review has no responses to persist.
    saveBeforeReload: itemsApp =>
      previewMode || request.request.state === 'review' ? Promise.resolve(true) : saveLearnosityResponses(itemsApp),
    getContainer: () => document.getElementById('learnosity_assess'),
    onReady: (itemsApp, telemetry) => {
      void checkTokenRefresh();
      if (request.request.state !== 'review') {
        itemsApp.on('item:load', () => {
          const progress = itemsApp.assessApp().getProgress();
          if (progress === 1) {
            setDisableSubmition(false);
          }
        });

        // Preview never saves or writes back — the confirmation modal short-circuits
        // submit, so we skip the save/submit/persist listeners entirely.
        if (!previewMode) {
          itemsApp.on('test:submit', function () {
            itemsApp.save();
          });
          itemsApp.on('test:submit:success', () => {
            posthog.capture('completed_skill_challenge');
            const sentryTraceId =
              Sentry.getActiveSpan()?.spanContext().traceId ?? Sentry.getCurrentScope().getPropagationContext().traceId;
            assessmentSubmitted(
              sessionId,
              'skill',
              getAccessTokenSilently,
              logout,
              posthog,
              undefined,
              sentryTraceId,
              request.request.user_id,
            );
          });

          itemsApp.on('test:save:error', e => {
            console.log('Learnosity assessment save error');
            telemetry.captureError(e, 'save_progress');
          });
        }
      }
    },
  });
  const mathRecoverySaving = mathRecovery === 'saving';

  useLayoutEffect(() => {
    // Keep the saved snapshot stable until recovery either fails or reloads.
    // Assess replaces its own container, so lock the React-owned parent instead.
    if (playerHostRef.current) playerHostRef.current.inert = mathRecoverySaving;
  }, [mathRecoverySaving]);

  useEffect(() => {
    if (status !== 'idle') setLoading(status === 'initializing' || status === 'retry_wait');
    if (status !== 'ready') setDisableSubmition(true);
  }, [status, setLoading]);

  useEffect(() => {
    if (scores && submitComplete) {
      const attemptInfo = attemptInfoFromScores(scores);
      const { totalScore } = attemptInfo;

      // Logged from the submission itself, rather than from the skill state settling afterwards, so
      // that a retake of the same skill at the same score is its own observation. Named by skill
      // rather than by flag: which experiment arm the student was in is resolved at analysis time.
      const skill = skillCode ?? 'unknown';
      logObservations('skill_metrics', [
        { subject: skill, type: 'assessment_pass', value: totalScore >= 8 ? 1 : 0 },
        { subject: skill, type: 'assessment_score', value: totalScore },
        { subject: skill, type: 'assessment_attempt_num', value: previousAttempts.length + 1 },
      ]);

      if (isWeeklyGoalEnabled && isWeeklyGoalElegible && weeklyGoalOverrideData && totalScore >= PASSING_SCORE) {
        //first update lastAddedGoal to trigger the modal with the new skill
        ctx?.setLastAddedGoal(weeklyGoalOverrideData);
        setTimeout(() => {
          //then add the override to update the goal values in the header
          ctx?.addGoalOverride(weeklyGoalOverrideData);
        }, 10000);
      }

      setAttemptInfo(attemptInfo);
      setCurrentStatus({
        classname: mapSkillStatus('', totalScore).className,
        status: mapSkillStatus('', totalScore)?.status,
      });

      setPreviousStatus(prev => [
        { id: 'SkillAssessmentNode:0', date: new Date().toISOString(), score: totalScore },
        ...prev,
      ]);

      if (isRetakeCountdownFlagActive) {
        cooldownCtx?.startCooldown(skillCode || '', previousAttempts.length + 1);
      }

      setPreviousAttempts(prev => [
        {
          score: totalScore,
          activityId: request.request.activity_id,
          sessionId: request.request.session_id,
          sessionCompletedOn: new Date().toISOString(),
        },
        ...prev,
      ]);
      setAssessmentInProgress(false);
      changeState('review');
      setDisabledReviewModal(false);
      posthog.capture('assessment_submitted', { assessment_score: totalScore, assessment_passed: totalScore >= 8 });
    }
  }, [scores, submitComplete]);

  return (
    <>
      {loadingExit && (
        <div
          className="tw-absolute tw-w-[89vw] tw-rounded-xl tw-h-[100vh] tw-bg-white tw-flex tw-items-center tw-flex-col"
          style={{ zIndex: 6 }}
        >
          <ThreeDotLoader dark />
          <h4>Saving the progress...</h4>
        </div>
      )}
      <div id="singleSkillAssessment">
        <div className="tw-text-center tw-pt-5 tw-flex tw-items-center tw-justify-between tw-w-[97%] tw-mx-auto tw-mb-5">
          {!previewMode && !localStorage.getItem('skill_challenger_tooltip') && (
            <EphemeralMessage
              delay={6}
              text="The Skill Challenge has a new look!
          Same challenge, just easier to focus."
              ease={[0.34, 1, 1, 1]}
              duration={3}
              customStyle={{
                top: '15rem',
                left: '45vw',
              }}
            />
          )}

          <Button outlined label="Back to practice" className="tw-border-none" onClick={backToPracticeHandler} />
          <div className="tw-flex tw-items-center">
            <PencilRuler size={20} className="tw-mr-2 tw-font-bold" />
            <h1>Skill challenge</h1>
          </div>
          {!previewMode && !localStorage.getItem('skill_challenger_tooltip') && (
            <EphemeralMessage
              delay={12}
              text="You can go back to practice anytime. Your progress is saved automatically."
              ease={[0.34, 1, 1, 1]}
              duration={3}
              onAnimationEnd={() => {
                localStorage.setItem('skill_challenger_tooltip', 'true');
              }}
              customStyle={{
                top: '15rem',
                left: '10vw',
              }}
            />
          )}
          {!previewMode && (
            <Button
              outlined
              label="Save and exit"
              disabled={status !== 'ready'}
              className="tw-border-none"
              onClick={saveAndExitHandler}
              tooltip="Saves progress and exits to home"
              tooltipOptions={{ position: 'top' }}
            />
          )}
        </div>
        {/* Keep a stable React host because Assess replaces the following container. */}
        <div>
          <LearnosityMathRecoveryNotice recovery={mathRecovery} onSaveAndReload={retryMathRecovery} />
        </div>
        <div ref={playerHostRef}>
          <div id="learnosity_assess" className="!tw-relative tw-z-[2]"></div>
        </div>
        <div className="tw-flex tw-justify-end">
          <Button
            style={{ position: 'relative', top: -20, zIndex: 2 }}
            label="Finish assessment"
            disabled={disableSubmition || mathRecoverySaving}
            className="tw-mr-8 tw-mb-4"
            onClick={verifyResponsesAndOpenConfirmation}
          />

          <Dialog visible={confirmationModalState} onHide={() => setConfirmationModalState(false)} showHeader={false}>
            <SubmitConfirmationModalContent
              request={request}
              itemsApp={itemsAppRef}
              setAlreadySubmitted={setAlreadySubmitted}
              setConfirmationModalState={setConfirmationModalState}
              incompleteQuestions={incompleteQuestions}
              setAssessmentInProgress={setAssessmentInProgress}
              setAttemptInfo={setAttemptInfo}
              setScores={setScores}
              setSubmitComplete={setSubmitComplete}
              mathRecoverySaving={mathRecoverySaving}
            />
          </Dialog>
        </div>
      </div>
    </>
  );
};

export default AssessmentWrapper;
